import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query"
import { ApiError } from "./api"

// Surface every settled API failure as a real console.error so nothing fails
// silently. Mirrors apps/admin's query.ts, but exposed as a FACTORY: TanStack
// Start calls createRouter() once per SSR request, so each request needs its
// own QueryClient (a shared singleton would leak one user's cache into the
// next request). On the client there's effectively one instance.
function logApiError(kind: "query" | "mutation", error: unknown, key: unknown) {
  if (error instanceof ApiError) {
    console.error(
      `[api] ${kind} failed (${error.status}${error.code ? ` ${error.code}` : ""}): ${error.message}`,
      { status: error.status, code: error.code, key, body: error.body },
    )
  } else {
    console.error(`[api] ${kind} failed:`, error, { key })
  }
}

export function makeQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => logApiError("query", error, query.queryKey),
    }),
    mutationCache: new MutationCache({
      onError: (error, _vars, _ctx, mutation) =>
        logApiError("mutation", error, mutation.options.mutationKey),
    }),
    defaultOptions: {
      queries: {
        retry: (failureCount, err) => {
          if (
            err instanceof ApiError &&
            (err.status === 401 || err.status === 403)
          )
            return false
          return failureCount < 2
        },
        staleTime: 30_000,
        refetchOnWindowFocus: true,
      },
      mutations: {
        retry: false,
      },
    },
  })
}
